import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Star, StarIcon, Camera, Send, CheckCircle } from "lucide-react";
import { makeAuthenticatedRequest } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface Vendor {
  id: number;
  name: string;
  businessName: string;
  category: string;
  rating: number;
  reviewCount: number;
}

interface Review {
  id: number;
  vendorId: number;
  rating: number;
  title: string;
  review: string;
  photos: string[];
  createdAt: string;
  vendor: {
    name: string;
    businessName: string;
    category: string;
  };
}

export default function FeedbackPage() {
  const [selectedVendor, setSelectedVendor] = useState<number | null>(null);
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [reviewText, setReviewText] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const { toast } = useToast();

  // Fetch vendors from wedding
  const { data: vendors = [], isLoading: vendorsLoading, error: vendorsError } = useQuery({
    queryKey: ['/api/wedding-vendors'],
    queryFn: async () => {
      const response = await makeAuthenticatedRequest('/api/wedding-vendors');
      if (!response.ok) {
        throw new Error('Failed to fetch vendors');
      }
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    },
  });

  // Fetch existing reviews
  // GET /api/vendor-reviews (server/routes/clientVendorReviews.ts) — the couple's own reviews.
  // A failed read throws, so it is never shown as "No reviews yet" (it used to read a 404 body).
  const { data: reviews = [], isError: reviewsError, refetch: refetchReviews } = useQuery<Review[]>({
    queryKey: ['/api/vendor-reviews'],
    queryFn: async () => {
      const response = await makeAuthenticatedRequest('/api/vendor-reviews');
      if (!response.ok) throw new Error('Failed to load your reviews');
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    },
  });

  const submitReviewMutation = useMutation({
    mutationFn: async (reviewData: {
      vendorId: number;
      rating: number;
      title: string;
      review: string;
      photos: File[];
    }) => {
      const formData = new FormData();
      formData.append('vendorId', reviewData.vendorId.toString());
      formData.append('rating', reviewData.rating.toString());
      formData.append('title', reviewData.title);
      formData.append('review', reviewData.review);
      
      reviewData.photos.forEach((photo, index) => {
        formData.append(`photo_${index}`, photo);
      });

      const response = await makeAuthenticatedRequest('/api/vendor-reviews', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) throw new Error('Failed to submit review');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/vendor-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['/api/wedding-vendors'] });
      setSelectedVendor(null);
      setRating(0);
      setTitle("");
      setReviewText("");
      setPhotos([]);
      toast({
        title: "Review Submitted",
        description: "Thank you for your feedback! Your review has been submitted successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to submit review. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newPhotos = Array.from(e.target.files);
      setPhotos(prev => [...prev, ...newPhotos].slice(0, 5)); // Max 5 photos
    }
  };

  const removePhoto = (index: number) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitReview = () => {
    if (!selectedVendor || rating === 0 || !reviewText.trim()) {
      toast({
        title: "Missing Information",
        description: "Please select a vendor, provide a rating, and write a review.",
        variant: "destructive",
      });
      return;
    }

    submitReviewMutation.mutate({
      vendorId: selectedVendor,
      rating,
      title,
      review: reviewText,
      photos,
    });
  };

  const renderStars = (currentRating: number, interactive: boolean = false) => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= currentRating 
                ? "text-yellow-400 fill-yellow-400" 
                : "text-gray-300"
            } ${interactive ? "cursor-pointer hover:text-yellow-400" : ""}`}
            onClick={interactive ? () => setRating(star) : undefined}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="p-6 lg:p-8 bg-gradient-to-br from-gray-900 via-black to-gray-900 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Share Your Feedback</h1>
          <p className="text-gray-400">
            Help other couples by sharing your experience with our vendors
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Write New Review */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center space-x-2">
                <Send className="w-5 h-5 text-[#B8860B]" />
                <span>Write a Review</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Vendor Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Select Vendor
                </label>
                <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto">
                  {vendorsLoading ? (
                    <div className="text-center py-4">
                      <div className="animate-spin w-6 h-6 border-2 border-[#B8860B] border-t-transparent rounded-full mx-auto"></div>
                      <p className="text-gray-400 text-sm mt-2">Loading vendors...</p>
                    </div>
                  ) : vendorsError ? (
                    <div className="text-center py-4">
                      <p className="text-red-400 text-sm">Failed to load vendors</p>
                      <p className="text-gray-500 text-xs">No vendors assigned to your wedding yet</p>
                    </div>
                  ) : vendors.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-gray-400 text-sm">No vendors found</p>
                      <p className="text-gray-500 text-xs">Contact your wedding planner to assign vendors</p>
                    </div>
                  ) : (
                    vendors.map((vendor: Vendor) => (
                      <div
                        key={vendor.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedVendor === vendor.id
                          ? "border-[#B8860B] bg-[#B8860B]/10"
                          : "border-gray-600 hover:border-gray-500"
                        }`}
                        onClick={() => setSelectedVendor(vendor.id)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-white font-medium">{vendor.businessName || vendor.name}</p>
                            <p className="text-gray-400 text-sm">{vendor.category}</p>
                          </div>
                          <Badge variant="secondary">
                            {vendor.rating}/5 ({vendor.reviewCount})
                          </Badge>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {selectedVendor && (
                <>
                  {/* Rating */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Your Rating
                    </label>
                    {renderStars(rating, true)}
                  </div>

                  {/* Review Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Review Title (Optional)
                    </label>
                    <Input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g., Exceptional photography service"
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>

                  {/* Review Text */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Your Review *
                    </label>
                    <Textarea
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      placeholder="Share your experience with this vendor..."
                      rows={4}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>

                  {/* Photo Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Add Photos (Optional)
                    </label>
                    <div className="space-y-2">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                        id="photo-upload"
                      />
                      <label
                        htmlFor="photo-upload"
                        className="flex items-center justify-center w-full p-4 border border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-gray-500"
                      >
                        <Camera className="w-5 h-5 text-gray-400 mr-2" />
                        <span className="text-gray-400">Upload Photos ({photos.length}/5)</span>
                      </label>
                      
                      {photos.length > 0 && (
                        <div className="grid grid-cols-3 gap-2">
                          {photos.map((photo, index) => (
                            <div key={index} className="relative">
                              <img
                                src={URL.createObjectURL(photo)}
                                alt={`Upload ${index + 1}`}
                                className="w-full h-20 object-cover rounded"
                              />
                              <button
                                onClick={() => removePhoto(index)}
                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    onClick={handleSubmitReview}
                    disabled={submitReviewMutation.isPending}
                    className="w-full bg-[#B8860B] hover:bg-[#9A7209] text-white"
                  >
                    {submitReviewMutation.isPending ? "Submitting..." : "Submit Review"}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Previous Reviews */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-[#B8860B]" />
                <span>Your Reviews</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {reviewsError ? (
                  <div className="text-center py-8" role="alert">
                    <p className="text-gray-300">Your reviews could not load.</p>
                    <Button type="button" variant="outline" className="mt-3" onClick={() => refetchReviews()}>Try again</Button>
                  </div>
                ) : reviews.length > 0 ? (
                  reviews.map((review: Review) => (
                    <div key={review.id} className="p-4 bg-gray-700 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-white font-medium">
                            {review.vendor?.businessName || review.vendor?.name || 'Vendor'}
                          </p>
                          <p className="text-gray-400 text-sm">{review.vendor?.category}</p>
                        </div>
                        {renderStars(review.rating)}
                      </div>
                      
                      {review.title && (
                        <p className="text-[#B8860B] font-medium mb-1">{review.title}</p>
                      )}
                      
                      <p className="text-gray-300 text-sm mb-2">{review.review}</p>
                      
                      {review.photos && review.photos.length > 0 && (
                        <div className="grid grid-cols-3 gap-1 mb-2">
                          {review.photos.slice(0, 3).map((photo, index) => (
                            <img
                              key={index}
                              src={photo}
                              alt={`Review photo ${index + 1}`}
                              className="w-full h-16 object-cover rounded"
                            />
                          ))}
                        </div>
                      )}
                      
                      <p className="text-gray-500 text-xs">
                        {new Date(review.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Star className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">No reviews yet</p>
                    <p className="text-gray-500 text-sm">Start by writing your first review!</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}